import {css} from "@emotion/react";
import {Stack, Typography, TextField, Button, Checkbox, FormControlLabel, Link} from "@mui/material";
import {useContext, useState, useCallback, FormEvent} from "react";
import {AuthContext} from "../../context/auth.context";
import {Conditions} from "../static";
import {LoginFooter} from "../footer";
import {PasswordTextField} from "../inputs/password";
import {Registered} from "./registered";
import {ConfigurationContext} from "../../App";

export const Register = ({email}: { email?: string }) => {
    const {register} = useContext(AuthContext);
    const [configuration] = useContext(ConfigurationContext);
    const [{firstName, secondName, surname, phone, password, password2}, setCredentials] =
        useState({firstName: '', secondName: '', surname: '',
            phone: '', password: '', password2: ''});
    const [registered, setRegistered] = useState(false);
    const [personalInfoAgreed, setPersonalInfoAgreed] = useState(false);
    
    const requireConfirmation = configuration?.requirePersonalInfoProcessingConfirmation === true;
    const agreementText = configuration?.textOfPersonalInfoProcessingAgreement || '';
    const policyLink = configuration?.linkToPersonalInfoProcessingPolicy || '';

    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();

        register(email!, password, password2, firstName, secondName, surname, phone).then(() => {
            setRegistered(true);
        })
    }, [email, firstName, secondName, surname, phone, password, password2, register]);

    if (registered) {
        return <Registered/>
    }

    return (
        <form onSubmit={handleSubmit}>
            <Stack maxWidth="816px" padding="3rem" alignItems="center" spacing={4}>
                <Typography variant="h1">Чтобы продолжить, зарегистрируйтесь:</Typography>

                <Stack width="454px" spacing={2}>
                    <TextField
                        fullWidth
                        value={firstName}
                        onChange={e => setCredentials(old => ({...old, firstName: e.target.value}))}
                        autoFocus
                        required
                        label="Имя"
                    />
                    <TextField
                        fullWidth
                        value={secondName}
                        onChange={e => setCredentials(old => ({...old, secondName: e.target.value}))}
                        required
                        label="Фамилия"
                    />
                    <TextField
                        fullWidth
                        value={surname}
                        onChange={e => setCredentials(old => ({...old, surname: e.target.value}))}
                        label="Отчество"
                    />
                    <TextField
                        fullWidth
                        value={phone}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.startsWith('+')) {
                                const digits = value.slice(1).replace(/\D/g, '');
                                setCredentials(old => ({...old, phone: '+' + digits}));
                            } else {
                                setCredentials(old => ({...old, phone: value.replace(/\D/g, '')}));
                            }
                        }}
                        required
                        label="Телефон"
                    />
                </Stack>

                <Stack direction="row" spacing={4}>
                    <Typography>Адрес электронной почты</Typography>
                    <Typography>{email}</Typography>
                </Stack>


                <Stack width="454px">
                    <PasswordTextField
                        autoComplete="password"
                        slotProps={{
                            input: {
                                autoComplete: 'new-password',
                            }
                        }}
                        error={!!password && !!password2 && password !== password2}
                        fullWidth
                        helperText={!!password && !!password2 && password !== password2 ? 'Пароли не совпадают' : undefined}
                        value={password}
                        onChange={e => setCredentials(old => ({
                            ...old,
                            password: e.target.value.replace(/[а-яёА-ЯЁ]/g, '')
                        }))}
                        label="Пароль"
                    />
                </Stack>

                <Stack width="454px">
                    <PasswordTextField
                        fullWidth
                        slotProps={{
                            input: {
                                autoComplete: 'new-password',
                            }
                        }}
                        error={!!password && !!password2 && password !== password2}
                        value={password2}
                        onChange={e => setCredentials(old => ({
                            ...old,
                            password2: e.target.value.replace(/[а-яёА-ЯЁ]/g, '')
                        }))}
                        label="Повторите пароль"
                    />
                </Stack>

                <Stack sx={css`
                    padding: 15px;
                    border-radius: 10px;
                    background-color: #126DF70D;
                `}>
                    <Typography variant='body2'>
                        В целях безопасности от вас периодически будет требоваться этот пароль.
                        Мы ценим ваше право на конфиденциальность. Мы не будем предоставлять или продавать вашу
                        информацию третьим лицам.
                    </Typography>
                </Stack>

                {requireConfirmation && (
                    <Stack width="454px" spacing={1}>
                        {agreementText && (
                            <Typography variant="body2">
                                {agreementText}
                            </Typography>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={personalInfoAgreed}
                                    onChange={(e) => setPersonalInfoAgreed(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {policyLink ? (
                                        <>
                                            Я согласен с{' '}
                                            <Link href={policyLink} target="_blank" rel="noopener noreferrer">
                                                политикой обработки персональных данных
                                            </Link>
                                        </>
                                    ) : (
                                        'Я согласен с политикой обработки персональных данных'
                                    )}
                                </Typography>
                            }
                        />
                    </Stack>
                )}

                <Button size="large" type="submit"
                        disabled={!firstName || !secondName || !phone
                            || !password || !password2 || password !== password2
                            || (requireConfirmation && !personalInfoAgreed)}>Продолжить</Button>

                <LoginFooter/>
                <Conditions/>
            </Stack>
        </form>
    )
}
